import { useEffect } from 'react'
import moment from 'moment'
import { FormProvider, useForm } from 'react-hook-form'
import useTranslation from 'next-translate/useTranslation'
import BagIcon from '@ancon/wildcat-ui/shared/icons/paper-bag-outline.svg'
import LinkIcon from '@ancon/wildcat-ui/shared/icons/link-outline.svg'

import FormInput from '../../../../app/components/FormInput'
import FormNumberModifierInput from '../../../../app/components/FormNumberModifierInput'
import BodyText from '../../../../app/components/BodyText'
import useAppDispatch from '../../../../../store/hooks/useAppDispatch'
import useAppSelector from '../../../../../store/hooks/useAppSelector'
import {
  preOrderNewGroupOrderNameSelector,
  preOrderNewGroupOrderSizeSelector,
  preOrderNewOrderDateSelector,
  preOrderNewOrderOrderFormatSelector,
  preOrderNewOrderSelectedOrderMethodSelector,
  preOrderSelectedRestaurantMaxAttendeeLimit,
} from '../../../store/preOrderSelectors'
import { setNewOrderDetails } from '../../../store/preOrderSlice'
import { GroupOrderFormState, PreOrderMethod } from '../../../types'
import { MinimumGroupOrderSize } from '../../../constants'
import preOrderConstraints from '../../../constraints/preOrderConstraints'
import isGroupOrderSupportedOrderFormat from '../../../utils/isGroupOrderSupportedOrderFormat'
import { outletsSelectedOutletListItemSelector } from '../../../../outlets/store/outletsSelectors'
import getOutletToday from '../../../../outlet/utils/getOutletToday'
import GroupOrderMaxAttendeeLimitNote from '../../newOrder/GroupOrderMaxAttendeeLimitNote'
import PanelOptionTiles from '../PanelOptionTiles'

import styles from './OrderTypeStep.module.scss'

/** A normal order, or a group order with a link for the team (name and size) */
export default function OrderTypeStep() {
  const { t } = useTranslation('preOrder')
  const dispatch = useAppDispatch()

  const orderMethod = useAppSelector(
    preOrderNewOrderSelectedOrderMethodSelector,
  )
  const orderFormat = useAppSelector(preOrderNewOrderOrderFormatSelector)
  const orderDate = useAppSelector(preOrderNewOrderDateSelector)
  const groupOrderName = useAppSelector(preOrderNewGroupOrderNameSelector)
  const groupOrderSize = useAppSelector(preOrderNewGroupOrderSizeSelector)
  const maxGroupSize = useAppSelector(
    preOrderSelectedRestaurantMaxAttendeeLimit,
  )
  const outlet = useAppSelector(outletsSelectedOutletListItemSelector)

  const isGroup = orderMethod === PreOrderMethod.Group

  const isGroupFormatSupported =
    orderFormat == null || isGroupOrderSupportedOrderFormat(orderFormat)
  // Group order requests need at least one day's notice. Reading today in
  // render is safe: the panel only mounts after a click, never on the server.
  // Today is the restaurant's.
  const isGroupDateTooSoon =
    !!orderDate && moment(orderDate).isSame(getOutletToday(outlet), 'day')

  function getGroupUnavailableReason() {
    if (!isGroupFormatSupported) {
      return t('newOrderPanel.type.groupFormatUnsupported')
    }

    if (isGroupDateTooSoon) {
      return t('newOrderPanel.type.groupNeedsNotice')
    }

    return null
  }

  const groupUnavailableReason = getGroupUnavailableReason()

  const form = useForm<GroupOrderFormState>({
    mode: 'onChange',
    defaultValues: {
      groupOrderName: groupOrderName ?? '',
      groupOrderSize: groupOrderSize ?? MinimumGroupOrderSize,
    },
  })

  const { watch } = form

  // Keep the store in step, the panel decides from it whether to continue
  useEffect(() => {
    const subscription = watch(values => {
      dispatch(
        setNewOrderDetails({
          groupOrderName: values.groupOrderName ?? null,
          groupOrderSize: values.groupOrderSize ?? null,
        }),
      )
    })

    return () => subscription.unsubscribe()
  }, [dispatch, watch])

  function handleSelectType(id: string) {
    const nextIsGroup = id === String(PreOrderMethod.Group)

    dispatch(
      setNewOrderDetails(
        nextIsGroup
          ? {
              orderMethod: PreOrderMethod.Group,
              groupOrderName: form.getValues('groupOrderName'),
              groupOrderSize: form.getValues('groupOrderSize'),
            }
          : { orderMethod: PreOrderMethod.Individual },
      ),
    )
  }

  // A group order that is no longer possible falls back to a normal order
  useEffect(() => {
    if (isGroup && groupUnavailableReason) {
      dispatch(setNewOrderDetails({ orderMethod: PreOrderMethod.Individual }))
    }
  }, [dispatch, groupUnavailableReason, isGroup])

  return (
    <>
      <PanelOptionTiles
        ariaLabel={t('newOrderPanel.steps.orderType')}
        selectedId={String(orderMethod)}
        onSelect={handleSelectType}
        tiles={[
          {
            id: String(PreOrderMethod.Individual),
            icon: <BagIcon />,
            label: t('newOrderPanel.type.order'),
            hint: t('newOrderPanel.type.orderHint'),
          },
          {
            id: String(PreOrderMethod.Group),
            icon: <LinkIcon />,
            label: t('newOrderPanel.type.group'),
            hint: groupUnavailableReason ?? t('newOrderPanel.type.groupHint'),
            disabled: !!groupUnavailableReason,
          },
        ]}
      />
      {isGroup && (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <FormProvider {...form}>
          <div className={styles.groupFields}>
            <BodyText color="body-1" fontSize="1.2rem">
              {t('newOrderPanel.type.groupDescription')}
            </BodyText>
            <FormInput
              fieldName="groupOrderName"
              label={t('newOrderPanel.type.orderName')}
              placeholder={t('newOrder.yourGroupOrderNameHere')}
              required
              rules={{
                required: t('common:validations.required'),
                maxLength: {
                  value: preOrderConstraints.max.groupOrderName,
                  message: t('common:validations.maxLength', {
                    limit: preOrderConstraints.max.groupOrderName,
                  }),
                },
              }}
            />
            <div className={styles.groupSize}>
              <BodyText as="span" className={styles.groupSizeLabel}>
                {t('newOrder.howManyInTheGroup')}
              </BodyText>
              <FormNumberModifierInput
                fieldName="groupOrderSize"
                rules={{
                  required: t('common:validations.required'),
                  min: {
                    value: MinimumGroupOrderSize,
                    message: t('validations.minGroupSize', {
                      minSize: MinimumGroupOrderSize,
                    }),
                  },
                  max: maxGroupSize
                    ? {
                        value: maxGroupSize,
                        message: t('validations.maxGroupSize', {
                          maxSize: maxGroupSize,
                        }),
                      }
                    : undefined,
                }}
              />
            </div>
            <GroupOrderMaxAttendeeLimitNote />
          </div>
        </FormProvider>
      )}
    </>
  )
}
